import { Injectable } from '@angular/core';
import {
  DataPageDefault,
  DataQueryParamPage,
  DataQueryParamFrom,
  DataQueryParamTo,
  DataStartedAt,
  DataQueryParamPersonName,
  DataPersonEntity,
} from '@edgvr-front/data/domain';
import {
  createEffect,
  Actions,
  ofType,
  EffectNotification,
  concatLatestFrom,
} from '@ngrx/effects';
import { getSelectors } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { asyncScheduler, Observable, throwError } from 'rxjs';
import {
  catchError,
  exhaustMap,
  mergeMap,
  observeOn,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { DataPersonPersonsListPageActions } from '../actions';
import { DataPersonsListRequestDTO } from '../dto';
import { DataService } from '../services';

const { selectRouteParams, selectQueryParams } = getSelectors();

@Injectable()
export class DataPersonPersonsListPageEffects {
  init$ = createEffect(() =>
    this.store.select(selectRouteParams).pipe(
      observeOn(asyncScheduler),
      concatLatestFrom(() => [this.store.select(selectQueryParams)]),
      mergeMap(([_, queryParams]) => [
        DataPersonPersonsListPageActions.dataPersonsList({
          page: Number(queryParams[DataQueryParamPage]) || DataPageDefault,
          personName: queryParams[DataQueryParamPersonName],
          from:
            (Number(queryParams[DataQueryParamFrom]) as DataStartedAt) ||
            undefined,
          to:
            (Number(queryParams[DataQueryParamTo]) as DataStartedAt) ||
            undefined,
        }),
      ])
    )
  );

  dataPersonsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DataPersonPersonsListPageActions.dataPersonsList),
      switchMap(({ page, personName, from, to }) =>
        this.dataService
          .dataPersonsList(
            new DataPersonsListRequestDTO({
              page,
              personName,
              from,
              to,
            })
          )
          .pipe(
            mergeMap((_) => [
              DataPersonPersonsListPageActions.dataPersonsListSuccess({
                dataPersons: _.data.dataPersons.map((_) =>
                  DataPersonEntity.factory(_)
                ),
                count: _.data.count,
                page,
              }),
            ]),
            catchError((_) => {
              this.store.dispatch(
                DataPersonPersonsListPageActions.dataPersonsListFailure()
              );

              return throwError(() => _);
            })
          )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly dataService: DataService
  ) {}

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return this.actions$.pipe(
      ofType(DataPersonPersonsListPageActions.init),
      exhaustMap(() =>
        resolvedEffects$.pipe(
          takeUntil(
            this.actions$.pipe(ofType(DataPersonPersonsListPageActions.destroy))
          )
        )
      )
    );
  }
}
